import React, { useRef, useState } from "react";
import { HomeOutlined, SearchOutlined } from "@ant-design/icons";
import { Breadcrumb, Table, Input, Button, Space, DatePicker, InputNumber } from "antd";
import dayjs from "dayjs";
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import "./browser_data_cash_invoice.css";
import { withDataCashInvoiceLogic, MappedDataType } from "../../components/hoc_data_cash_invoice";
import { useNavigate } from "react-router-dom";


type DataType = MappedDataType;
interface BrowserDataCashInvoiceProps {
    data: DataType[];
    isLoading: boolean;
}

type DataIndex = keyof DataType;

const getStatusInvoiceLabel = (status: string): string => {
    switch (status) {
        case '1':
            return 'Active';
        case '2':
            return 'Lunas';
        case '3':
            return 'Jatuh Tempo';
        default:
            return status;
    }
};

const parseDateString = (dateStr: string | null | undefined): Date | null => {
    if (!dateStr) return null;
    const parts = dateStr.split('/');
    if (parts.length !== 3) return null;
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    const date = new Date(year, month, day);
    return isNaN(date.getTime()) ? null : date;
};

const BrowserDataCashInvoice: React.FC<BrowserDataCashInvoiceProps> = ({ data, isLoading }) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const navigate = useNavigate();


    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const [pageSize, setPageSize] = useState<number>(10);

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };


    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };


    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        Close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            (record[dataIndex]
                ?.toString()
                .toLowerCase()
                .includes((value as string).toLowerCase())) ?? false,
        filterDropdownProps: {
            onOpenChange: (open) => {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const getColumnDateRangeFilterProps = (): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <DatePicker.RangePicker
                    value={selectedKeys[0] ? (selectedKeys[0] as unknown as [dayjs.Dayjs, dayjs.Dayjs]) : null}
                    onChange={(dates) => {
                        setSelectedKeys(dates ? [dates] as unknown as React.Key[] : []);
                    }}
                    format="DD/MM/YYYY"
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => confirm()}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Filter
                    </Button>
                    <Button
                        onClick={() => {
                            if (clearFilters) {
                                clearFilters();
                            }
                            confirm();
                        }}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) => {
            if (!value) return true;
            const [start, end] = value as unknown as [dayjs.Dayjs, dayjs.Dayjs];
            if (!start || !end) return true;
            const recordDate = parseDateString(record.invoiceDate);
            if (!recordDate) return false;
            const recordTime = new Date(recordDate.getFullYear(), recordDate.getMonth(), recordDate.getDate()).getTime();
            const startTime = start.startOf('day').toDate().getTime();
            const endTime = end.endOf('day').toDate().getTime();
            return recordTime >= startTime && recordTime <= endTime;
        },
    });

    const columns: TableColumnsType<DataType> = [
        {
            title: 'Receipt No',
            dataIndex: 'receiptNo',
            key: 'receiptNo',
            ...getColumnSearchProps('receiptNo'),
            sorter: (a, b) => a.receiptNo.localeCompare(b.receiptNo),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Status Invoice',
            dataIndex: 'statusInvoice',
            key: 'statusInvoice',
            filters: [
                { text: 'Active', value: '1' },
                { text: 'Lunas', value: '2' },
                { text: 'Jatuh Tempo', value: '3' },
            ],
            onFilter: (value, record) => record.statusInvoice === value,
            sorter: (a, b) => a.statusInvoice.localeCompare(b.statusInvoice),
            sortDirections: ['descend', 'ascend'],
            render: (text: string) => getStatusInvoiceLabel(text),
        },
        {
            title: 'Invoice No',
            dataIndex: 'invoiceNo',
            key: 'invoiceNo',
            ...getColumnSearchProps('invoiceNo'),
            sorter: (a, b) => a.invoiceNo.localeCompare(b.invoiceNo),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Invoice Date',
            dataIndex: 'invoiceDate',
            key: 'invoiceDate',
            ...getColumnDateRangeFilterProps(),
            sorter: (a, b) => (a.invoiceDate || '').localeCompare(b.invoiceDate || ''),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Customer Name',
            dataIndex: 'customerName',
            key: 'customerName',
            ...getColumnSearchProps('customerName'),
            sorter: (a, b) => a.customerName.localeCompare(b.customerName),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Warehouse',
            dataIndex: 'warehouse',
            key: 'warehouse',
            ...getColumnSearchProps('warehouse'),
            sorter: (a, b) => (a.warehouse || '').localeCompare(b.warehouse || ''),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Amount Invoice',
            dataIndex: 'amountInvoice',
            key: 'amountInvoice',
            ...getColumnSearchProps('amountInvoice'),
            sorter: (a, b) => a.amountInvoice - b.amountInvoice,
            sortDirections: ['descend', 'ascend'],
            render: (text: number) => new Intl.NumberFormat('id-ID').format(text),
        },
        {
            title: 'Amount Payment',
            dataIndex: 'amountPayment',
            key: 'amountPayment',
            ...getColumnSearchProps('amountPayment'),
            sorter: (a, b) => a.amountPayment - b.amountPayment,
            sortDirections: ['descend', 'ascend'],
            render: (text: number) => new Intl.NumberFormat('id-ID').format(text),
        },
        {
            title: 'Balance Credit',
            dataIndex: 'balanceCredit',
            key: 'balanceCredit',
            ...getColumnSearchProps('balanceCredit'),
            sorter: (a, b) => a.balanceCredit - b.balanceCredit,
            sortDirections: ['descend', 'ascend'],
            render: (text: number) => new Intl.NumberFormat('id-ID').format(text),
        },
        {
            title: 'Remark',
            dataIndex: 'remark',
            key: 'remark',
            ...getColumnSearchProps('remark'),
            sorter: (a, b) => (a.remark || '').localeCompare(b.remark || ''),
            sortDirections: ['descend', 'ascend'],
        },
    ];

    return (

        <div id="ilqt" style={transformStyle}>
            <div className="gjs-grid-row" id="i5kj">
                <div className="gjs-grid-column" id="iq93">
                    <Breadcrumb
                        items={[
                            {
                                title: <HomeOutlined />,
                            },
                            {
                                title: (
                                    <>
                                        <span>Browser Data Invoice</span>
                                    </>
                                ),
                            },
                            {
                                href: "#/dashboard/data_cash_invoice",
                                title: "Data Cash Invoice",
                            },
                        ]}
                    />
                </div>
            </div>

            <div className="gjs-grid-row" id="ih46t">
                <div className="gjs-grid-column" id="i8pm">
                    <Table
                        columns={columns}
                        dataSource={data}
                        loading={isLoading}
                        onRow={(record) => {
                            return {
                                onDoubleClick: () => {
                                    navigate('/dashboard/data_cash_invoice', { state: { record } });
                                },
                            };
                        }}
                        pagination={{
                            pageSize: pageSize,
                            pageSizeOptions: ['10', '100'],
                            showSizeChanger: true,
                            onShowSizeChange: (_, size) => setPageSize(size),
                            showTotal: (total) => (
                                <Space style={{ marginRight: 16 }}>
                                    <span>Total {total} items</span>
                                    <span>Max Tampil:</span>
                                    <InputNumber
                                        min={1}
                                        style={{ width: 70 }}
                                        value={pageSize}
                                        onChange={(val) => val && setPageSize(val)}
                                    />
                                </Space>
                            )
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default withDataCashInvoiceLogic(BrowserDataCashInvoice);